// src/modules/app-views/crm/pages/Boards/components/BoardsGroup.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Card, Tag, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { useDndContext, useDroppable } from '@dnd-kit/core';
import translateX from 'utils/translateX';
import DraggableBoardCard from './DraggableBoardCard';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BoardsGroup = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    boards,
    group,
    setSelectedGroup,
    setSelectedBoard,
    setShowBoardModal,
    onOpenBoard,
    onEditBoard,
    canManage,
  } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const groupUid = group?.uid || group?._id;
  const { setNodeRef, isOver } = useDroppable({ id: groupUid });
  const { active } = useDndContext();

  // Highlight when dragging within this group (active board belongs here)
  const isDraggingWithinGroup =
    !!active && (boards || []).some(b => (b.uid || b._id) === active.id);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleNewBoard = () => {
    setSelectedGroup(group);
    setSelectedBoard(null);
    setShowBoardModal(true);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const boardIds = (boards || []).map(b => b.uid || b._id);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: 140,
        borderRadius: 8,
        transition: 'background 200ms ease, outline 200ms ease',
        background: (isDraggingWithinGroup || (isOver && canManage)) ? token.colorPrimaryBg : 'transparent',
        outline: (isDraggingWithinGroup || (isOver && canManage)) ? `2px dashed ${token.colorPrimaryBorder}` : '2px dashed transparent',
      }}
    >
      <SortableContext items={boardIds} strategy={rectSortingStrategy}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
            padding: '8px 4px',
          }}
        >
          {(boards || []).map(board => {
            const boardUid = board.uid || board._id;
            return (
              <DraggableBoardCard
                key={boardUid}
                board={board}
                group={group}
                onOpenBoard={onOpenBoard}
                onEditBoard={onEditBoard}
                canManage={canManage}
              />
            );
          })}

          {/* Add new board button — only visible to admin and owner */}
          {canManage && <Card
            hoverable
            size="small"
            style={{
              height: '100%',
              minHeight: 120,
              marginBottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderStyle: 'dashed',
            }}
            onClick={handleNewBoard}
          >
            <div style={{ textAlign: 'center' }}>
              <PlusOutlined style={{ fontSize: 24, color: '#999' }} />
              <div className="mt-2 text-muted">{translateX('new_board')}</div>
            </div>
          </Card>}
        </div>
      </SortableContext>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BoardsGroup;
